/**
 * PatientSessionLockScreen.jsx — v237.6.0
 *
 * Patient-facing session lock. Shown when the patient navigates away and
 * returns (visibilitychange, ≥60s) AND a session PIN is set. Mirrors the MD
 * panel's SessionPinLockScreen: 4-digit keypad, 30-minute PIN window, 3-attempt
 * limit → full logout, and a "sign in with full credentials" fallback.
 *
 * The lock is triggered in usePrivacyScreen; this component only renders while
 * useUserStore.isLocked is true and handles verification + window expiry.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { OverlayPortal } from './ModalPortal'
import { Z_SECURITY } from '../utils/zLayers'
import { useNavigate } from 'react-router-dom'
import { Lock, Clock, LogOut, Delete, CheckCircle, AlertTriangle, Phone, ScanFace } from 'lucide-react'
import { useUserStore } from '../stores'
import { authAPI } from '../services/api'
import { useI18n } from '../i18n'
import { isLockExpired, pinWindowMs } from '../utils/sessionReturn'
import { biometricAvailable, verifyBiometric } from '../utils/biometric' // s114 Face ID unlock
import { getFaceIdPref } from '../utils/faceIdPref' // s114 Face ID opt-in (canvas F3)
import { safePath } from '../utils/safePath' // PROD-1313: a stored route is checked before navigate()

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const GREEN = '#66D170'
const PASTEL_GREEN = '#E1F5EE'

export default function PatientSessionLockScreen() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const user = useUserStore((s) => s.user)
  const lockedAt = useUserStore((s) => s.lockedAt)
  const lockedRoute = useUserStore((s) => s.lockedRoute)
  const unlockSession = useUserStore((s) => s.unlockSession)
  const incrementPinFailures = useUserStore((s) => s.incrementPinFailures)
  const logout = useUserStore((s) => s.logout)

  const [pin, setPin] = useState(['', '', '', ''])
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState('')
  const [windowExpired, setWindowExpired] = useState(false)

  // ★★ s114 (Dr. Rami 2026-10-22: "Face ID to login to the app"; his pick: UNLOCK, copy approved en + es). On the iPhone
  //   app with Face ID / Touch ID enrolled, the lock screen asks for it at once; a cancel or a failure leaves the PIN
  //   keypad exactly as it was. Face ID proves the phone's owner, the session itself is the one already signed in; the
  //   PIN window, the 3-attempt limit and "Sign in with full credentials" are unchanged. Never on the web.
  const [bioAvail, setBioAvail] = useState(false)
  const bioAskedRef = useRef(false)
  const unlockWithFaceId = useCallback(async () => {
    const ok = await verifyBiometric(t('lockScreen.faceIdReason'))
    if (!ok) return
    setSuccess(true)
    setTimeout(() => {
      unlockSession()
      if (lockedRoute && lockedRoute !== '/') {
        navigate(safePath(lockedRoute, '/home'), { replace: true })
      }
    }, 600)
  }, [t, unlockSession, lockedRoute, navigate])
  // ★★ s114 (Dr. Rami 2026-10-22, Face ID opt-in canvas approved): only when the patient TURNED Face ID ON (Settings or
  //   sign-up) for this account on this phone. Off, or not set: the PIN keypad alone, exactly as before.
  const faceIdOn = getFaceIdPref(user?.id)
  useEffect(() => {
    let alive = true
    if (!faceIdOn) return () => { alive = false }
    biometricAvailable().then((yes) => {
      if (!alive || !yes) return
      setBioAvail(true)
      if (!bioAskedRef.current) { bioAskedRef.current = true; unlockWithFaceId() }
    })
    return () => { alive = false }
  }, [unlockWithFaceId, faceIdOn])

  const pinWindowMinutes = user?.sessionPinWindow || 30
  const firstName = user?.firstName || ''

  // Countdown timer for the PIN window
  useEffect(() => {
    const updateTimer = () => {
      if (!lockedAt) return
      // ★★ One boundary, shared with `decideOnReturn`. The countdown is display
      //    only; `isLockExpired` is the verdict, so this screen and the return
      //    path cannot disagree about when 30 minutes is up.
      const now = Date.now()
      if (isLockExpired({ lockedAt, now, pinWindowMinutes })) {
        setWindowExpired(true)
        setTimeRemaining('0:00')
        return
      }
      const remaining = (pinWindowMs(pinWindowMinutes) - (now - lockedAt)) / 1000
      const mins = Math.floor(remaining / 60)
      const secs = Math.floor(remaining % 60)
      setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')}`)
    }
    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [lockedAt, pinWindowMinutes])

  // ★★★ Window expired → full sign-in, IMMEDIATELY. This used to wait 2000ms,
  //     which meant a patient past their PIN window sat looking at a live keypad
  //     on top of their own chart while the timer ran out. A lock that has
  //     outlived its window is not a lock you are allowed to keep typing into.
  //     v242.130.0: a return from >30 min away no longer reaches this screen at
  //     all — `decideOnReturn` sends it straight to sign-in — so this is now the
  //     belt-and-braces for a session that expires WHILE the keypad is up.
  useEffect(() => {
    if (windowExpired) {
      logout()
      navigate('/signin', { replace: true })
    }
  }, [windowExpired, logout, navigate])

  const handleVerify = useCallback(async (pinDigits) => {
    const fullPin = (pinDigits || pin).join('')
    if (fullPin.length !== 4) return

    setVerifying(true)
    setError('')
    try {
      const response = await authAPI.verifySessionPin(fullPin)
      if (response.valid) {
        setSuccess(true)
        setTimeout(() => {
          unlockSession()
          if (lockedRoute && lockedRoute !== '/') {
            navigate(safePath(lockedRoute, '/home'), { replace: true })
          }
        }, 1100)
      } else {
        const result = incrementPinFailures()
        if (result.forcedLogout) {
          setError(t('lockScreen.tooManyAttempts', 'Too many attempts. Signing out…'))
          setTimeout(() => navigate('/signin', { replace: true }), 1500)
        } else {
          const n = result.attemptsRemaining
          setError(
            t('lockScreen.incorrectPin', 'Incorrect PIN.') +
            ' ' + n + ' ' +
            (n === 1
              ? t('lockScreen.attemptRemaining', 'attempt remaining.')
              : t('lockScreen.attemptsRemaining', 'attempts remaining.'))
          )
          setPin(['', '', '', ''])
        }
      }
    } catch (err) {
      setError(t('lockScreen.couldNotVerify', 'Could not verify PIN. Please try again.'))
      setPin(['', '', '', ''])
    }
    setVerifying(false)
  }, [pin, unlockSession, lockedRoute, navigate, incrementPinFailures, t])

  const handleDigit = useCallback((digit) => {
    if (verifying || success) return
    setError('')
    setPin((prev) => {
      const next = [...prev]
      const emptyIdx = next.findIndex((d) => d === '')
      if (emptyIdx === -1) return prev
      next[emptyIdx] = digit
      if (emptyIdx === 3) setTimeout(() => handleVerify(next), 150)
      return next
    })
  }, [verifying, success, handleVerify])

  const handleBackspace = useCallback(() => {
    if (verifying || success) return
    setError('')
    setPin((prev) => {
      const next = [...prev]
      const lastFilledIdx = next.reduce((acc, d, i) => (d !== '' ? i : acc), -1)
      if (lastFilledIdx >= 0) next[lastFilledIdx] = ''
      return next
    })
  }, [verifying, success])

  // Physical keyboard support (desktop / tablet)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key)
      else if (e.key === 'Backspace') handleBackspace()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [handleDigit, handleBackspace])

  const handleFullLogin = () => {
    logout()
    navigate('/signin', { replace: true })
  }

  const lockedAgoText = (() => {
    if (!lockedAt) return ''
    const mins = Math.floor((Date.now() - lockedAt) / (1000 * 60))
    if (mins < 1) return t('lockScreen.lockedJustNow', 'Locked just now')
    if (mins === 1) return t('lockScreen.lockedOneMin', 'Locked 1 minute ago')
    return t('lockScreen.lockedMins', 'Locked {n} minutes ago').replace('{n}', mins)
  })()

  // Window-expired screen
  if (windowExpired) {
    return (
      <OverlayPortal>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: Z_SECURITY, backgroundColor: 'rgba(21, 53, 79, 0.97)' }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
            <AlertTriangle className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {t('lockScreen.windowExpiredTitle', 'PIN window expired')}
          </h2>
          <p className="text-sm text-slate-500">
            {t('lockScreen.windowExpiredBody', 'Your session PIN has expired. Please sign in again.')}
          </p>
        </div>
      </div>
      </OverlayPortal>
    )
  }

  // Success screen
  if (success) {
    return (
      <OverlayPortal>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: Z_SECURITY, backgroundColor: 'rgba(21, 53, 79, 0.97)' }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center animate-scaleIn">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: PASTEL_GREEN }}>
            <CheckCircle className="w-7 h-7" style={{ color: '#0F6E56' }} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-1">
            {firstName
              ? t('lockScreen.welcomeBackName', 'Welcome back, {name}').replace('{name}', firstName)
              : t('lockScreen.welcomeBack', 'Welcome back')}
          </h2>
          <p className="text-sm text-slate-500">{t('lockScreen.resuming', 'Resuming your session…')}</p>
        </div>
      </div>
      </OverlayPortal>
    )
  }

  return (
    <OverlayPortal>
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: Z_SECURITY, backgroundColor: 'rgba(21, 53, 79, 0.97)' }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
        <div className="h-1" style={{ backgroundColor: BLUE }} />

        <div className="p-8 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: ICE_BLUE }}>
            <Lock className="w-6 h-6" style={{ color: BLUE }} />
          </div>

          <h2 className="text-lg font-semibold text-slate-900 mb-1">
            {firstName
              ? t('lockScreen.welcomeBackName', 'Welcome back, {name}').replace('{name}', firstName)
              : t('lockScreen.title', 'Session locked')}
          </h2>
          <p className="text-xs text-slate-400 mb-5">{lockedAgoText}</p>

          {/* PIN dots */}
          <div className="flex gap-3 justify-center mb-5">
            {pin.map((digit, i) => (
              <div
                key={i}
                className="w-12 h-14 rounded-lg border-2 flex items-center justify-center text-xl font-semibold transition-all"
                style={{
                  borderColor: digit ? BLUE : '#E2E8F0',
                  backgroundColor: digit ? ICE_BLUE : 'white',
                  color: NAVY,
                }}
              >
                {digit ? '●' : ''}
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-400 mb-4">
            {t('lockScreen.enterPin', 'Enter your 4-digit PIN')}
          </p>

          {error && (
            <div className="flex items-center justify-center gap-1.5 mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600 font-medium">{error}</p>
            </div>
          )}

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto mb-5">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'].map((key, i) => {
              if (key === '') return <div key={i} />
              if (key === 'back') {
                return (
                  <button
                    key={i}
                    onClick={handleBackspace}
                    className="h-12 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100 active:bg-slate-200"
                    aria-label="Backspace"
                  >
                    <Delete className="w-5 h-5 text-slate-400" />
                  </button>
                )
              }
              return (
                <button
                  key={i}
                  onClick={() => handleDigit(key)}
                  disabled={verifying}
                  className="h-12 rounded-lg border text-lg font-medium transition-colors hover:bg-slate-50 active:bg-slate-100"
                  style={{ borderColor: '#E2E8F0', color: NAVY }}
                >
                  {key}
                </button>
              )
            })}
          </div>

          {bioAvail && faceIdOn && (
            <button
              type="button"
              data-lock-faceid
              onClick={unlockWithFaceId}
              disabled={verifying}
              className="w-full max-w-[220px] mx-auto mb-4 h-11 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2"
              style={{ backgroundColor: NAVY }}
            >
              <ScanFace className="w-4 h-4" />
              {t('lockScreen.useFaceId')}
            </button>
          )}

          {/* Timer */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <p className="text-xs text-slate-400">
              {t('lockScreen.pinExpiresIn', 'PIN expires in')}{' '}
              <span className="font-medium" style={{ color: BLUE }}>{timeRemaining}</span>
            </p>
          </div>

          {/* Full login fallback */}
          <button
            onClick={handleFullLogin}
            className="flex items-center justify-center gap-1.5 mx-auto text-xs text-slate-400 hover:text-slate-600 transition-colors py-1.5 px-3 rounded-full border border-slate-200 hover:border-slate-300"
          >
            <LogOut className="w-3 h-3" />
            {t('lockScreen.fullLogin', 'Sign in with full credentials')}
          </button>

          {/* ★★ R27 / PROD-435 (Dr. Rami 2026-10-03) + D455(3), patient P1: a locked session
              can still call 911. Tap-to-call, at the bottom, and "Call 911" is the emergency
              sheet's own key (emergency.call911), so the two can never word it differently.
              The 911 sheet itself stays on Z_SECURITY (check-security-tier ARM 2 / ARM 5). */}
          <a
            href="tel:911"
            className="flex items-center justify-center gap-1.5 mx-auto mt-3 text-xs font-semibold py-1.5 px-3"
            style={{ color: '#B91C1C', textDecoration: 'none' }}
          >
            <Phone className="w-3 h-3" />
            {t('lockScreen.emergencyPrompt')} {t('emergency.call911')}
          </a>
        </div>
      </div>
    </div>
    </OverlayPortal>
  )
}
